import React, { useState } from 'react';
import {
  ShieldCheck,
  Loader2,
  AlertCircle,
  Save,
  Check,
  RotateCcw,
  Trash2,
  ChevronDown,
  ChevronUp,
  FileText,
  Globe,
  User,
  Info,
  AlertTriangle,
  CheckCircle,
  ShieldAlert,
  XCircle,
  HelpCircle,
} from 'lucide-react';
import { vetSpeaker } from '../../lib/openai';
import type { SpeakerVettingJSON, VettingCategory, VettingAssessment } from '../../lib/openai';
import { useSpeakerVetting } from '../../hooks/useSpeakerVetting';
import type { SpeakerVettingRecord } from '../../hooks/useSpeakerVetting';
import { ConfirmationModal } from '../ConfirmationModal';
import { SaveNotification } from '../SaveNotification';

type InputMode = 'name' | 'paste' | 'url';

const SUBJECT_TYPES = ['Speaker', 'Author', 'Conference', 'Ministry', 'Bible Study'] as const;

const CATEGORY_LABELS: Record<string, string> = {
  viewOfScripture: 'View of Scripture',
  gospelClarity: 'Gospel Clarity',
  christology: 'Christology',
  anthropology: 'Anthropology',
  sexualEthics: 'Sexual Ethics',
  churchAuthority: 'Church Authority',
  charismPractice: 'Charismatic Practice',
  progressiveIndicators: 'Progressive Theology Indicators',
  prosperityIndicators: 'Prosperity Gospel Indicators',
  ecumenicalConcerns: 'Ecumenical Concerns',
  politicalOverreach: 'Political Overreach',
  toneAndHumility: 'Tone & Humility',
};

const CATEGORY_DESCRIPTIONS: Record<string, string> = {
  viewOfScripture: 'Inerrancy, authority, sufficiency of Scripture',
  gospelClarity: 'Sin, grace, substitutionary atonement, faith alone',
  christology: 'Deity and humanity of Christ, exclusivity of salvation',
  anthropology: 'Human nature, sin, free will vs. sovereignty',
  sexualEthics: 'Marriage as man/woman, gender, sexual ethics',
  churchAuthority: 'Ecclesiology, elder authority, accountability',
  charismPractice: 'Cessationism/continuationism, miraculous gifts',
  progressiveIndicators: 'Signs of drift toward progressive Christianity',
  prosperityIndicators: 'Wealth-as-blessing, name-it-claim-it theology',
  ecumenicalConcerns: 'Inappropriate unity with heterodox movements',
  politicalOverreach: 'Using ministry platform for political agenda',
  toneAndHumility: 'Teachable spirit, willingness to be corrected',
};

function assessmentConfig(assessment: VettingAssessment) {
  switch (assessment) {
    case 'No Concerns':
      return { icon: CheckCircle, className: 'text-emerald-700 bg-emerald-50 border-emerald-200', dot: 'bg-emerald-500' };
    case 'Minor Concerns':
      return { icon: AlertTriangle, className: 'text-blue-700 bg-blue-50 border-blue-200', dot: 'bg-blue-500' };
    case 'Moderate Concerns':
      return { icon: AlertTriangle, className: 'text-amber-700 bg-amber-50 border-amber-200', dot: 'bg-amber-500' };
    case 'Significant Concerns':
      return { icon: ShieldAlert, className: 'text-red-700 bg-red-50 border-red-200', dot: 'bg-red-500' };
    case 'Insufficient Data':
      return { icon: HelpCircle, className: 'text-slate-600 bg-slate-50 border-slate-200', dot: 'bg-slate-400' };
  }
}

function recommendationConfig(rec: SpeakerVettingJSON['recommendation']) {
  switch (rec) {
    case 'Generally Orthodox':
      return { icon: CheckCircle, className: 'bg-emerald-100 text-emerald-800 border-emerald-300' };
    case 'Use with Discernment':
      return { icon: AlertTriangle, className: 'bg-blue-100 text-blue-800 border-blue-300' };
    case 'Concerns Present':
      return { icon: AlertTriangle, className: 'bg-amber-100 text-amber-800 border-amber-300' };
    case 'Significant Concerns':
      return { icon: ShieldAlert, className: 'bg-red-100 text-red-800 border-red-300' };
  }
}

function VettingCategoryRow({ categoryKey, data }: { categoryKey: string; data: VettingCategory }) {
  const [expanded, setExpanded] = useState(false);
  const config = assessmentConfig(data.assessment);
  const Icon = config.icon;

  return (
    <div className={`rounded-xl border ${config.className} overflow-hidden`}>
      <div
        className="flex items-center justify-between p-4 cursor-pointer hover:bg-white/40 transition-colors"
        onClick={() => setExpanded(v => !v)}
      >
        <div className="flex items-center gap-3 min-w-0">
          <div className={`shrink-0 h-2 w-2 rounded-full ${config.dot}`} />
          <div className="min-w-0">
            <p className="text-sm font-semibold truncate">{CATEGORY_LABELS[categoryKey] || categoryKey}</p>
            {!expanded && <p className="text-xs opacity-70 truncate">{CATEGORY_DESCRIPTIONS[categoryKey]}</p>}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0 ml-3">
          <span className="hidden sm:flex items-center gap-1 text-xs font-medium">
            <Icon className="h-3.5 w-3.5" />
            {data.assessment}
          </span>
          {expanded ? <ChevronUp className="h-4 w-4 opacity-60" /> : <ChevronDown className="h-4 w-4 opacity-60" />}
        </div>
      </div>
      {expanded && (
        <div className="px-4 pb-4 border-t border-current/10 space-y-2 pt-3">
          <p className="text-sm">{data.notes}</p>
          {data.evidence && (
            <div className="bg-white/60 rounded-lg px-3 py-2">
              <p className="text-xs font-medium opacity-70 mb-1">Evidence / Source</p>
              <p className="text-xs italic">"{data.evidence}"</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function VettingResult({
  data,
  onSave,
  onReset,
  isSaved,
  saving,
}: {
  data: SpeakerVettingJSON;
  onSave: () => void;
  onReset: () => void;
  isSaved: boolean;
  saving: boolean;
}) {
  const rec = recommendationConfig(data.recommendation);
  const RecIcon = rec.icon;

  const concernCount = Object.values(data.categories).filter(
    c => c.assessment === 'Significant Concerns' || c.assessment === 'Moderate Concerns'
  ).length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-xl border border-sand/20 shadow-sm p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <ShieldCheck className="h-5 w-5 text-electric" />
              <h2 className="text-xl font-bold text-midnight">{data.subjectName}</h2>
            </div>
            <p className="text-sm text-slate">{data.subjectType}</p>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <button
              onClick={onReset}
              className="flex items-center gap-2 px-3 py-2 rounded-lg bg-sand/20 text-slate hover:bg-sand/40 transition-colors text-sm"
            >
              <RotateCcw className="h-4 w-4" />New Profile
            </button>
            <button
              onClick={onSave}
              disabled={isSaved || saving}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-electric text-white hover:bg-electric/90 disabled:opacity-50 transition-colors text-sm font-medium"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : isSaved ? <><Check className="h-4 w-4" />Saved</> : <><Save className="h-4 w-4" />Save</>}
            </button>
          </div>
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-3">
          <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-sm font-medium ${rec.className}`}>
            <RecIcon className="h-4 w-4" />
            {data.recommendation}
          </span>
          {concernCount > 0 && (
            <span className="text-xs text-slate bg-sand/30 px-2.5 py-1 rounded-full">
              {concernCount} categor{concernCount === 1 ? 'y' : 'ies'} with moderate or significant concerns
            </span>
          )}
        </div>

        <p className="mt-4 text-sm text-midnight leading-relaxed">{data.summary}</p>
      </div>

      {/* Red flags */}
      {data.redFlags.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-5">
          <h3 className="text-sm font-semibold text-red-800 mb-3 flex items-center gap-2">
            <XCircle className="h-4 w-4" />
            Documented Concerns
          </h3>
          <div className="space-y-2">
            {data.redFlags.map((flag, i) => (
              <div key={i} className="flex items-start gap-2 text-sm text-red-800">
                <span className="shrink-0 mt-0.5">•</span>
                <span>{flag}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Strengths */}
      {data.strengths.length > 0 && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-5">
          <h3 className="text-sm font-semibold text-emerald-800 mb-3 flex items-center gap-2">
            <CheckCircle className="h-4 w-4" />
            Strengths Noted
          </h3>
          <div className="space-y-2">
            {data.strengths.map((s, i) => (
              <div key={i} className="flex items-start gap-2 text-sm text-emerald-800">
                <span className="shrink-0 mt-0.5">•</span>
                <span>{s}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 12 category assessments */}
      <div>
        <h3 className="text-sm font-semibold text-midnight mb-3 flex items-center gap-2">
          <ShieldCheck className="h-4 w-4 text-electric" />
          Theological Assessment by Category
        </h3>
        <div className="space-y-2">
          {Object.entries(data.categories).map(([key, val]) => (
            <VettingCategoryRow key={key} categoryKey={key} data={val} />
          ))}
        </div>
      </div>

      {/* Sources */}
      {data.sourcesAnalyzed.length > 0 && (
        <div className="bg-sand/20 border border-sand/30 rounded-xl p-4">
          <p className="text-xs font-medium text-slate mb-2">Sources Analyzed</p>
          <div className="flex flex-wrap gap-2">
            {data.sourcesAnalyzed.map((s, i) => (
              <span key={i} className="text-xs bg-white border border-sand/40 rounded-full px-2.5 py-1 text-midnight">{s}</span>
            ))}
          </div>
        </div>
      )}

      {/* Disclaimer */}
      <div className="flex items-start gap-2.5 bg-sand/20 border border-sand/40 rounded-lg px-4 py-3">
        <Info className="h-4 w-4 text-slate shrink-0 mt-0.5" />
        <p className="text-xs text-slate">{data.disclaimer}</p>
      </div>
    </div>
  );
}

function HistoryItem({
  record,
  onDelete,
}: {
  record: SpeakerVettingRecord;
  onDelete: (id: string) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const rec = recommendationConfig(record.content.recommendation);
  const RecIcon = rec.icon;
  return (
    <div className="bg-white rounded-xl border border-sand/20 overflow-hidden">
      <div
        className="flex items-center justify-between p-4 cursor-pointer hover:bg-sand/10 transition-colors"
        onClick={() => setExpanded(v => !v)}
      >
        <div className="flex items-center gap-3 min-w-0">
          <div className="shrink-0 p-2 bg-indigo-100 rounded-lg">
            <ShieldCheck className="h-4 w-4 text-indigo-600" />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-midnight truncate">{record.content.subjectName}</p>
            <p className="text-xs text-slate">{record.content.subjectType} · {new Date(record.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span className={`hidden sm:inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-xs font-medium ${rec.className}`}>
            <RecIcon className="h-3 w-3" />
            {record.content.recommendation}
          </span>
          <button
            onClick={e => { e.stopPropagation(); onDelete(record.id); }}
            className="p-2 text-slate hover:text-red-600 rounded-lg hover:bg-red-50 transition-all"
            title="Delete"
          >
            <Trash2 className="h-4 w-4" />
          </button>
          {expanded ? <ChevronUp className="h-4 w-4 text-slate" /> : <ChevronDown className="h-4 w-4 text-slate" />}
        </div>
      </div>
      {expanded && (
        <div className="border-t border-sand/20 p-5">
          <VettingResult
            data={record.content}
            onSave={() => {}}
            onReset={() => {}}
            isSaved={true}
            saving={false}
          />
        </div>
      )}
    </div>
  );
}

export function SpeakerVetting() {
  const [subjectName, setSubjectName] = useState('');
  const [subjectType, setSubjectType] = useState<typeof SUBJECT_TYPES[number]>('Speaker');
  const [inputMode, setInputMode] = useState<InputMode>('name');
  const [pasteContent, setPasteContent] = useState('');
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [analyzing, setAnalyzing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<SpeakerVettingJSON | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saveNotification, setSaveNotification] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);

  const { analyses, saveAnalysis, deleteAnalysis } = useSpeakerVetting();

  const handleAnalyze = async () => {
    setError(null);
    setAnalyzing(true);
    setResult(null);
    setSaved(false);
    try {
      const res = await vetSpeaker({
        name: subjectName,
        subjectType,
        content: inputMode === 'paste' ? pasteContent : undefined,
        websiteUrl: inputMode === 'url' ? websiteUrl : undefined,
      });
      if (res.success) {
        setResult(res.content as SpeakerVettingJSON);
      } else {
        setError(res.error || 'Analysis failed. Please try again.');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred.');
    } finally {
      setAnalyzing(false);
    }
  };

  const handleSave = async () => {
    if (!result || saved) return;
    setSaving(true);
    try {
      await saveAnalysis({
        title: `${result.subjectName} (${result.subjectType})`,
        content: result,
      });
      setSaved(true);
      setSaveNotification(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteAnalysis(deleteTarget);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete.');
    } finally {
      setDeleteTarget(null);
    }
  };

  const canAnalyze = !analyzing && subjectName.trim().length > 1 && (
    inputMode === 'name' ||
    (inputMode === 'paste' && pasteContent.trim().length > 20) ||
    (inputMode === 'url' && websiteUrl.trim())
  );

  const INPUT_TABS: { id: InputMode; label: string; icon: React.ElementType }[] = [
    { id: 'name', label: 'Name Only', icon: User },
    { id: 'paste', label: 'Paste Content', icon: FileText },
    { id: 'url', label: 'Website URL', icon: Globe },
  ];

  return (
    <div>
      <div className="flex flex-wrap justify-between items-start gap-3 mb-4">
        <div>
          <h2 className="text-xl font-semibold text-midnight">Speaker / Conference Vetting</h2>
          <p className="text-sm text-slate mt-0.5">Generate a theological discernment profile for ministry decisions</p>
        </div>
      </div>

      {/* Disclaimer banner */}
      <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 mb-6">
        <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
        <p className="text-xs text-amber-800 leading-relaxed">
          <strong>Discernment Aid, Not a Verdict.</strong> This tool provides a sober, evidence-based assessment to assist pastoral judgment — not to condemn. AI analysis has knowledge cutoffs and may not reflect recent statements or changes in position. Always verify with primary sources and pray before making ministry decisions.
        </p>
      </div>

      {/* Input panel */}
      {!result && (
        <div className="bg-white border border-sand/30 rounded-xl shadow-sm p-5 mb-6">
          {/* Subject name + type */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
            <div className="sm:col-span-2">
              <label className="block text-xs font-medium text-slate mb-1">Subject Name <span className="text-red-500">*</span></label>
              <input
                type="text"
                placeholder="e.g. John Smith, The Passion Conference, Bethel Music"
                value={subjectName}
                onChange={e => setSubjectName(e.target.value)}
                className="w-full px-3 py-2 border border-sand/40 rounded-lg text-sm text-midnight bg-white focus:ring-2 focus:ring-electric/30 focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate mb-1">Type</label>
              <select
                value={subjectType}
                onChange={e => setSubjectType(e.target.value as typeof SUBJECT_TYPES[number])}
                className="w-full px-3 py-2 border border-sand/40 rounded-lg text-sm text-midnight bg-white focus:ring-2 focus:ring-electric/30 focus:outline-none"
              >
                {SUBJECT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
          </div>

          {/* Input mode tabs */}
          <div className="mb-4">
            <label className="block text-xs font-medium text-slate mb-2">Additional Context (optional but improves accuracy)</label>
            <div className="flex gap-1 bg-sand/20 rounded-lg p-1 mb-4 w-fit">
              {INPUT_TABS.map(tab => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setInputMode(tab.id)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                      inputMode === tab.id
                        ? 'bg-white text-midnight shadow-sm'
                        : 'text-slate hover:text-midnight'
                    }`}
                  >
                    <Icon className="h-3.5 w-3.5" />
                    {tab.label}
                  </button>
                );
              })}
            </div>

            {inputMode === 'name' && (
              <p className="text-xs text-slate">The AI will analyze {subjectName || 'the subject'} based on its training knowledge of public statements, books, and ministry history. Best for well-known speakers and conferences.</p>
            )}

            {inputMode === 'paste' && (
              <textarea
                value={pasteContent}
                onChange={e => setPasteContent(e.target.value)}
                placeholder="Paste sermon transcripts, social media posts, articles, book excerpts, ministry statements, or any other relevant content…"
                rows={7}
                className="w-full px-3 py-2 border border-sand/40 rounded-lg text-sm text-midnight bg-white focus:ring-2 focus:ring-electric/30 focus:outline-none resize-none"
              />
            )}

            {inputMode === 'url' && (
              <div className="space-y-2">
                <input
                  type="url"
                  placeholder="https://speaker-website.com/about or /beliefs"
                  value={websiteUrl}
                  onChange={e => setWebsiteUrl(e.target.value)}
                  className="w-full px-3 py-2 border border-sand/40 rounded-lg text-sm text-midnight bg-white focus:ring-2 focus:ring-electric/30 focus:outline-none"
                />
                <p className="text-xs text-slate">The page will be fetched server-side and its text content analyzed alongside the AI's training knowledge. Works best on "About," "Beliefs," or "What We Believe" pages.</p>
              </div>
            )}
          </div>

          {error && (
            <div className="flex items-center gap-2 text-red-600 text-sm bg-red-50 border border-red-200 rounded-lg px-3 py-2 mb-4">
              <AlertCircle className="h-4 w-4 shrink-0" />
              {error}
            </div>
          )}

          <button
            onClick={handleAnalyze}
            disabled={!canAnalyze}
            className="flex items-center gap-2 px-5 py-2.5 bg-electric text-white text-sm font-medium rounded-lg hover:bg-electric/90 disabled:opacity-50 transition-colors"
          >
            {analyzing ? (
              <><Loader2 className="h-4 w-4 animate-spin" />Generating Profile…</>
            ) : (
              <><ShieldCheck className="h-4 w-4" />Generate Vetting Profile</>
            )}
          </button>
        </div>
      )}

      {/* Loading */}
      {analyzing && (
        <div className="flex flex-col items-center justify-center h-48 gap-3">
          <Loader2 className="h-8 w-8 animate-spin text-electric" />
          <p className="text-sm text-slate">Evaluating theological profile…</p>
        </div>
      )}

      {/* Result */}
      {result && !analyzing && (
        <VettingResult
          data={result}
          onSave={handleSave}
          onReset={() => { setResult(null); setSaved(false); setError(null); }}
          isSaved={saved}
          saving={saving}
        />
      )}

      {/* History */}
      {!result && !analyzing && analyses.length > 0 && (
        <div className="mt-2">
          <div className="flex items-center gap-2 mb-3">
            <ShieldCheck className="h-4 w-4 text-indigo-600" />
            <h3 className="text-sm font-semibold text-midnight">Saved Profiles</h3>
            <span className="text-xs text-slate bg-sand/40 px-2 py-0.5 rounded-full">{analyses.length}</span>
          </div>
          <div className="space-y-3">
            {analyses.map(a => (
              <HistoryItem key={a.id} record={a} onDelete={setDeleteTarget} />
            ))}
          </div>
        </div>
      )}

      {!result && !analyzing && analyses.length === 0 && (
        <div className="text-center py-14 bg-white rounded-xl border border-sand/20">
          <ShieldCheck className="h-12 w-12 text-slate/30 mx-auto mb-3" />
          <p className="text-slate text-sm max-w-sm mx-auto">Enter a speaker's name, paste their content, or provide a website URL to generate a theological discernment profile across 12 ministry-relevant categories.</p>
        </div>
      )}

      <SaveNotification
        show={saveNotification}
        onHide={() => setSaveNotification(false)}
        message="Vetting profile saved"
        type="success"
      />

      <ConfirmationModal
        isOpen={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title="Delete Profile"
        message="Are you sure you want to permanently delete this vetting profile?"
        confirmText="Delete"
        icon={<Trash2 className="h-5 w-5 text-red-600" />}
      />
    </div>
  );
}
